import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { useAuth } from '../store/auth';
import {
  LayoutGrid,
  BarChart3,
  Target,
  MousePointer,
  Megaphone,
  Gift,
  Users,
  UserCircle,
  Network,
  Zap,
  Globe,
  Send,
  ScrollText,
  Settings,
  LogOut,
  DollarSign,
  Link2,
  Shield,
  Menu,
  X,
  Bell,
} from 'lucide-react';
import { ThemeSwitcher } from './ThemeSwitcher';
import { usePollMultiplier } from '../hooks/usePollMultiplier';
import { useNavGroups } from '../hooks/useNavGroups';
import { Badge } from './ui/Badge';

const adminGroups: Array<{ group: string; items: Array<{ to: string; label: string; icon: typeof LayoutGrid; superOnly?: boolean }> }> = [
  { group: 'DASHBOARD', items: [{ to: '/overviews', label: 'Overview', icon: LayoutGrid }] },
  {
    group: 'REPORTING',
    items: [
      { to: '/statistics', label: 'Statistics', icon: BarChart3 },
      { to: '/conversions', label: 'Conversions', icon: Target },
      { to: '/clicks', label: 'Clicks', icon: MousePointer },
    ],
  },
  {
    group: 'TRAFFIC',
    items: [
      { to: '/campaigns', label: 'Campaigns', icon: Megaphone },
      { to: '/offers', label: 'Offers', icon: Gift },
      { to: '/networks', label: 'Affiliate Networks', icon: Network },
      { to: '/tracking-links', label: 'Tracking Links', icon: Link2 },
    ],
  },
  {
    group: 'SOURCES',
    items: [
      { to: '/publishers', label: 'Traffic Sources', icon: UserCircle },
      { to: '/automation', label: 'Automation', icon: Zap },
    ],
  },
  {
    group: 'SYSTEM',
    items: [
      { to: '/tracker-domains', label: 'Domains', icon: Globe },
      { to: '/forwarding', label: 'Forwarding', icon: Send, superOnly: true },
      { to: '/postback-logs', label: 'Postbacks', icon: ScrollText, superOnly: true },
      { to: '/audit-log', label: 'Audit Log', icon: Shield, superOnly: true },
      { to: '/users', label: 'Users', icon: Users },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

const userGroups: Array<{ group: string; items: Array<{ to: string; label: string; icon: typeof LayoutGrid }> }> = [
  { group: 'DASHBOARD', items: [{ to: '/overviews', label: 'Overview', icon: LayoutGrid }] },
  {
    group: 'REPORTING',
    items: [
      { to: '/statistics', label: 'Statistics', icon: BarChart3 },
      { to: '/conversions', label: 'Conversions', icon: Target },
      { to: '/clicks', label: 'Clicks', icon: MousePointer },
      { to: '/tracking-links', label: 'Tracking Links', icon: Link2 },
      { to: '/earnings', label: 'My Earnings', icon: DollarSign },
    ],
  },
];

const SECTION_LABEL: Record<string, string> = {
  overviews: 'DASHBOARD',
  statistics: 'REPORTING', conversions: 'REPORTING', clicks: 'REPORTING', earnings: 'REPORTING',
  campaigns: 'TRAFFIC', offers: 'TRAFFIC', networks: 'TRAFFIC', 'tracking-links': 'TRAFFIC',
  publishers: 'SOURCES', automation: 'SOURCES',
  'tracker-domains': 'SYSTEM', forwarding: 'SYSTEM', 'postback-logs': 'SYSTEM',
  'audit-log': 'SYSTEM', users: 'SYSTEM', settings: 'SYSTEM',
};
const PAGE_LABEL: Record<string, string> = {
  overviews: 'Overview', statistics: 'Statistics', conversions: 'Conversions', clicks: 'Clicks',
  earnings: 'My Earnings', campaigns: 'Campaigns', offers: 'Offers', networks: 'Affiliate Networks',
  'tracking-links': 'Tracking Links', publishers: 'Traffic Sources', automation: 'Automation',
  'tracker-domains': 'Domains', forwarding: 'Forwarding', 'postback-logs': 'Postbacks',
  'audit-log': 'Audit Log', users: 'Users', settings: 'Settings',
};

export function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const groups = (user?.role === 'admin' ? adminGroups : userGroups)
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (item) => !('superOnly' in item && item.superOnly) || user?.isSuperAdmin,
      ),
    }))
    .filter((g) => g.items.length > 0);

  const { collapsed, toggle, isGroupActive } = useNavGroups(location.pathname);

  // Clock ticks every second for live HH:MM:SS format
  const [clockTime, setClockTime] = useState(() =>
    new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setClockTime(new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Online-users widget — polls every 5 MINUTES
  const onlineQuery = useQuery({
    queryKey: ['online-count'],
    queryFn: () =>
      apiClient.get<{ total: number; admins: number; publishers: number }>(
        '/api/v1/auth/online-count',
      ),
    refetchInterval: 300_000,
    refetchIntervalInBackground: false,
    enabled: !!user,
  });

  // Alert bell (P0-B): polling unread count tiap 30s × poll multiplier
  const pollMult = usePollMultiplier();
  const unreadAlerts = useQuery({
    queryKey: ['alerts-unread'],
    queryFn: () =>
      apiClient.get<{ count: number }>('/api/v1/alerts/alerts/unread-count'),
    refetchInterval: 30_000 * pollMult,
    refetchIntervalInBackground: false,
    enabled: user?.role === 'admin',
  });

  // Mobile sidebar drawer state
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock page scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [mobileOpen]);

  // Derive breadcrumb from route (SECTION / PAGE)
  const getBreadcrumb = () => {
    const seg = location.pathname.split('/').filter(Boolean)[0] ?? '';
    return `${SECTION_LABEL[seg] ?? 'DASHBOARD'} / ${PAGE_LABEL[seg] ?? (seg ? seg.toUpperCase() : 'Overview')}`;
  };

  return (
    <div className="h-screen overflow-hidden flex bg-bg-primary">
      {/* Nav Rail - 220px width */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[220px] shrink-0 cyber-panel border-r border-neon-cyan/18 flex flex-col transition-transform duration-200 ease-out lg:static lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ width: '220px' }}
      >
        {/* Logo area */}
        <div className="p-4 border-b border-neon-cyan/18 flex items-center justify-between">
          <div className="font-display font-bold text-sm tracking-widest text-text-primary uppercase flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-neon-cyan shadow-neon-cyan animate-pulse" />
            RJ TRACK
          </div>
          {mobileOpen && (
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="lg:hidden text-text-tertiary hover:text-text-primary p-1"
              aria-label="Close menu"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Navigation items */}
        <nav className="flex-1 p-3 space-y-3 overflow-y-auto w-full">
          {groups.map((g) => {
            const tos = g.items.map((i) => i.to);
            const active = isGroupActive(g.group, tos);
            const open = !collapsed[g.group] || active;
            return (
              <div key={g.group}>
                <button
                  type="button"
                  onClick={() => toggle(g.group)}
                  className="w-full flex items-center justify-between px-2 py-1 text-[10px] font-display uppercase tracking-[0.2em] text-text-tertiary hover:text-text-primary transition-colors"
                >
                  <span>{g.group}</span>
                  <span className={`transition-transform ${open ? '' : '-rotate-90'}`}>▾</span>
                </button>
                {open && (
                  <div className="mt-1 space-y-1">
                    {g.items.map(({ to, label, icon: Icon }) => {
                      const isItemActive = location.pathname === to;
                      return (
                        <NavLink
                          key={to}
                          to={to}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-r text-[13px] font-medium transition-all border-l-2 ${
                            isItemActive
                              ? 'bg-[rgba(0,255,225,0.08)] text-neon-cyan border-neon-cyan'
                              : 'bg-transparent text-text-secondary border-transparent hover:text-text-primary hover:bg-bg-tertiary hover:border-border-subtle'
                          }`}
                        >
                          <Icon size={15} className="shrink-0" />
                          <span className="truncate">{label}</span>
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer: avatar + name + role + logout */}
        <div className="p-3 border-t border-neon-cyan/18 space-y-2">
          <div className="flex items-center gap-2.5 p-2 rounded bg-bg-secondary border border-border-subtle/50">
            <div className="w-8 h-8 rounded bg-neon-cyan/10 border border-neon-cyan/40 flex items-center justify-center text-neon-cyan font-display font-bold text-xs uppercase shrink-0">
              {user?.username?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate text-text-primary">{user?.displayName || user?.username || 'Unknown'}</div>
              <div className="text-[10px] text-text-tertiary uppercase tracking-wider font-mono">{user?.role || 'user'}</div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="text-text-tertiary hover:text-neon-cyan transition-colors p-1.5 hover:bg-neon-cyan/5 rounded"
              title="Logout"
            >
              <LogOut size={15} />
            </button>
          </div>
          <NavLink
            to="/profile"
            className="flex items-center gap-2 px-2 py-1 text-[11px] text-text-tertiary hover:text-neon-cyan transition-colors"
          >
            <Settings size={12} /> Profile & Theme
          </NavLink>
        </div>
      </aside>

      {/* Mobile-only backdrop */}
      {mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          aria-label="Close menu overlay"
        />
      )}

      {/* Main content area */}
      <main className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Top bar - sticky 48px */}
        <header className="sticky top-0 z-30 h-12 px-4 sm:px-6 flex items-center justify-between gap-4 border-b border-neon-cyan/18 bg-bg-primary/80 backdrop-blur-sm shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger - mobile only */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-text-secondary hover:text-text-primary p-1.5 border border-neon-cyan/18 rounded"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
            
            {/* Breadcrumb */}
            <span className="text-xs font-mono text-text-tertiary uppercase tracking-wider truncate">
              {getBreadcrumb()}
            </span>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            {/* Live clock */}
            <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-mono text-text-secondary">
              <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse" />
              {clockTime}
            </span>
            
            {/* Online count */}
            {onlineQuery.data && (
              <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border border-neon-cyan/30 bg-neon-cyan/5 text-neon-cyan rounded-sm"
                title={
                  user?.role === 'admin'
                    ? `${onlineQuery.data.admins} admin · ${onlineQuery.data.publishers} publisher`
                    : `${onlineQuery.data.total} users active in last 5 min`
                }
              >
                {onlineQuery.data.total} online
              </span>
            )}
            
            {/* Alerts button */}
            {user?.role === 'admin' && (
              <button
                type="button"
                onClick={() => navigate('/automation?tab=alerts')}
                className="relative p-1.5 text-text-secondary hover:text-neon-cyan transition-colors border border-neon-cyan/18 rounded"
                title="Alerts"
              >
                <Bell size={16} />
                {(unreadAlerts.data?.count ?? 0) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 rounded-full bg-neon-magenta text-black text-[9px] font-bold flex items-center justify-center">
                    {unreadAlerts.data!.count > 9 ? '9+' : unreadAlerts.data!.count}
                  </span>
                )}
              </button>
            )}
            
            {/* Theme toggle */}
            <ThemeSwitcher />
          </div>
        </header>

        {/* Page content - clean responsive padding */}
        <div className="flex-1 p-4 sm:p-5 lg:p-6 overflow-y-auto w-full">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
